/**
 * Optional, cookieless usage statistics: Cloudflare Web Analytics.
 *
 * Nothing here runs unless (a) a site token is configured AND (b) the visitor
 * has opted in (src/lib/consent.tsx). The token reaches the page either as a
 * <meta> injected by the web Worker from `ANALYTICS_BEACON_TOKEN` (production)
 * or, for local testing, from `VITE_CF_BEACON_TOKEN`.
 *
 * The Worker's CSP does not allow-list the beacon's origin. Instead it mints a
 * nonce per response, puts it in `script-src` and in a second <meta>, and this
 * loader copies it onto the beacon <script>. Only that element may run; a beacon
 * injected into the HTML by anyone else (Cloudflare's "Automatic setup", a
 * proxy, an extension) carries no nonce and is refused by the browser.
 */
import { isBearerLinkLocation } from "./utils";

const BEACON_SRC = "https://static.cloudflareinsights.com/beacon.min.js";

function metaContent(name: string): string | null {
  return document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`)?.content?.trim() || null;
}

export function analyticsToken(): string | null {
  const env = (import.meta.env.VITE_CF_BEACON_TOKEN as string | undefined)?.trim();
  return metaContent("cf-beacon-token") || env || null;
}

let injected = false;
// Capture the entry URL before a route loader can redirect it. Keep this document analytics-free
// even after the token leaves the address bar (the beacon also observes navigation/referrer state).
let bearerDocument = typeof window !== "undefined" && isBearerLinkLocation(window.location);

/** Idempotent. Withdrawal of consent is honoured on the next page load (see consent.tsx). */
export function loadAnalytics(token: string): void {
  bearerDocument ||= isBearerLinkLocation(window.location);
  if (bearerDocument) return;
  if (injected) return;
  injected = true;
  const s = document.createElement("script");
  s.defer = true;
  s.src = BEACON_SRC;
  // Absent only outside the Worker (Vite dev server, no CSP). Set both the IDL attribute and the
  // content attribute: the IDL setter is what the spec guarantees for script-created elements.
  const nonce = metaContent("cf-beacon-nonce");
  if (nonce) {
    s.nonce = nonce;
    s.setAttribute("nonce", nonce);
  }
  // `spa: true` → route changes count as page views in a single-page app.
  s.dataset.cfBeacon = JSON.stringify({ token, spa: true });
  document.head.appendChild(s);
}

/**
 * A loaded or still-loading SPA beacon cannot be safely removed: its history listeners survive.
 * Leave that document before putting a bearer URL in history. Without a beacon, retain SPA navigation
 * but suppress any later consent effect for this document, including the auth round trip.
 */
export function reloadForBearerLink(path: string): boolean {
  const target = new URL(path, window.location.href);
  if (!isBearerLinkLocation(target)) return false;
  bearerDocument = true;
  if (!injected) return false;
  window.location.assign(target.href);
  return true;
}
